import React, { useState, useMemo } from "react";
import UniversalDataInspector from "../../../common/parsers/UniversalDataInspector";
import { UniversalLinkParser } from "../../../common/utils/UniversalLinkParser";
import { SaveESG_taxonomyController } from "./controllers/SaveESG_taxonomyController";
import { GenericFileUploader } from "../../../upload/services/genericFileUploader";

export default function WorkflowValidationView({
  selectedFile,
  results,
  isProcessing,
  onValidate,
  onProceed
}) {
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const [isPromoted, setIsPromoted] = useState(false);
  const [promoBatchId, setPromoBatchId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // --- AUDIT CONTROLLER STATE ---
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const auditResults = useMemo(() => {
    if (!results?.esgTree?.fullPot) return null;

    let filtered = results.esgTree.fullPot;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        (n.business_chapter?.toLowerCase().includes(q)) ||
        (n.label?.toLowerCase().includes(q))
      );
    }

    const availableRoles = [...new Set(filtered.map(n => n.role_id))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    if (roleFilter) {
      filtered = filtered.filter(n => n.role_id === roleFilter);
    }

    const chapters = [...new Set(filtered.map(n => n.business_chapter))];
    const levels = [...new Set(filtered.map(n => n.level))].sort((a, b) => a - b);

    const totalRows = filtered.length;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedRows = filtered.slice(startIndex, startIndex + rowsPerPage);

    return {
      paginatedRows,
      totalRows,
      availableRoles,
      chapters,
      maxLevel: levels.length > 0 ? levels[levels.length - 1] : 0,
    };
  }, [results, searchQuery, roleFilter, currentPage]);

  const handleGenerateTree = () => {
    if (!results?.esgTree) {
      alert("Validation data not found. Please run 'START VALIDATION' first.");
      return;
    }
    setShowJson(true);
  };

  const handleFinalSave = async () => {
    if (isSaving || isPromoted) return;

    if (!results?.esgTree?.fullPot) {
      console.error("❌ [VIEW_ERROR]: No ESG Tree data found to save.");
      alert("Validation data missing. Please run 'START VALIDATION' first.");
      return;
    }

    setSaveMessage("");
    setIsSaving(true);

    try {
      const payload = results.esgTree.fullPot;

      const config = {
        bucketName: "esg-staging-clean",
        fileName: `ESG_Taxonomy_Sync_${Date.now()}.json`,
        labels: ["Automated", "Validated", "Clean-Room"],
        batchType: 1
      };

      console.log("📤 [VIEW] Passing parameters to GenericFileUploader...", config);

      const response = await GenericFileUploader.uploadGeneratedData(payload, config);

      if (response?.success) {
        console.log("✅ [VIEW] Save successful:", response.batchId);
        setIsPromoted(true);
        setPromoBatchId(response.batchId || null);
        setSaveMessage("Next step: return to Refresh Taxonomy to review the uploaded dataset.");
      } else {
        throw new Error(response?.message || "Upload did not complete successfully.");
      }
    } catch (err) {
      console.error("🚨 [VIEW_SAVE_FAILED]:", err);
      setSaveMessage(`Save failed: ${err?.message || "Unknown upload error."}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedFile) return (
    <div className="p-5 text-center text-muted small italic bg-light rounded">
      Select an asset from the inventory to begin structural analysis.
    </div>
  );

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      <div className="card-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-primary small">Structural Analysis</h6>
        <span className="badge bg-dark font-monospace" style={{ fontSize: "10px" }}>{selectedFile?.filename}</span>
      </div>

      <div className="card-body p-0">
        {!results ? (
          <div className="text-center py-5">
            <button className="btn btn-primary btn-sm fw-bold px-5" onClick={onValidate} disabled={isProcessing}>
              {isProcessing ? "ANALYZING..." : "START VALIDATION"}
            </button>
          </div>
        ) : (
          <div className="animate__animated animate__fadeIn">
            <div className="worksheet-list">
              {results.worksheets?.map((ws, i) => (
                <div key={i} className="border-bottom">
                  <div
                    className={`d-flex align-items-center px-3 py-1 cursor-pointer ${expandedSheet === ws.name ? "bg-light" : ""}`}
                    onClick={() => setExpandedSheet(expandedSheet === ws.name ? null : ws.name)}
                  >
                    <div className="flex-grow-1 small fw-bold">{ws.name}</div>
                    <div className="text-muted extra-small">{ws.rowCount} Rows</div>
                  </div>
                  {expandedSheet === ws.name && (
                    <div className="p-2 border-top bg-white">
                      <UniversalDataInspector worksheet={ws} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!showJson && (
              <div className="p-3 bg-white border-top d-grid">
                <button className="btn btn-primary fw-bold" onClick={handleGenerateTree}>
                  GENERATE ESG TREE
                </button>
              </div>
            )}

            {showJson && auditResults && (
              <div className="p-3 bg-light border-top animate__animated animate__fadeIn">
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-dark text-white py-2 d-flex justify-content-between">
                    <h6 className="mb-0 extra-small fw-bold text-uppercase">Taxonomy Integrity Audit</h6>
                    <span className="extra-small text-success fw-bold">{auditResults.totalRows} Matches</span>
                  </div>
                  <div className="card-body p-3 bg-white">
                    <div className="row g-2 mb-3">
                      <div className="col-md-7">
                        <label className="extra-small fw-bold text-muted text-uppercase">1. Search Chapter/Label</label>
                        <input
                          type="text"
                          className="form-control form-control-sm shadow-none"
                          placeholder="Deep search strings..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="extra-small fw-bold text-muted text-uppercase">2. Refine by Role</label>
                        <select
                          className="form-select form-select-sm fw-bold shadow-none"
                          value={roleFilter}
                          onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="">All Roles ({auditResults.availableRoles.length})</option>
                          {auditResults.availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">CHAPTERS</div>
                        <div className="fw-bold small">{auditResults.chapters.length}</div>
                      </div></div>
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">MAX DEPTH</div>
                        <div className="fw-bold small">Lvl {auditResults.maxLevel}</div>
                      </div></div>
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">PAGE</div>
                        <div className="fw-bold small">{currentPage} / {Math.ceil(auditResults.totalRows / rowsPerPage) || 1}</div>
                      </div></div>
                    </div>

                    <div className="table-responsive border rounded bg-white overflow-auto" style={{ maxHeight: "400px" }}>
                      <table className="table table-hover table-sm mb-0 align-middle" style={{ fontSize: "11px" }}>
                        <thead className="table-light text-muted text-uppercase">
                          <tr>
                            <th className="ps-3" style={{ width: "45px" }}>Lvl</th>
                            <th style={{ width: "80px" }}>Role</th>
                            <th>Business Chapter / Label / References</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditResults.paginatedRows.map((n, i) => (
                            <tr key={i}>
                              <td className="ps-3 fw-bold text-primary">L{n.level}</td>
                              <td className="font-monospace text-muted" style={{ fontSize: "9px" }}>{n.role_id}</td>
                              <td>
                                <div className="fw-bold text-dark text-truncate" style={{ maxWidth: "400px" }}>{n.business_chapter}</div>
                                <div className="text-muted small mb-1">{n.label}</div>
                                <div className="mt-1">
                                  {UniversalLinkParser(n.legal_reference)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 px-1">
                      <span className="extra-small text-muted italic">Showing {auditResults.paginatedRows.length} of {auditResults.totalRows}</span>
                      <div className="btn-group">
                        <button
                          className="btn btn-xs btn-outline-dark fw-bold"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(p => p - 1)}
                        >
                          PREV
                        </button>
                        <button
                          className="btn btn-xs btn-outline-dark fw-bold"
                          disabled={currentPage * rowsPerPage >= auditResults.totalRows}
                          onClick={() => setCurrentPage(p => p + 1)}
                        >
                          NEXT
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded border bg-dark p-3 mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  <pre className="text-success mb-0" style={{ fontSize: "10px", lineHeight: "1.1" }}>
                    {JSON.stringify(results.esgTree.fullPot, null, 2)}
                  </pre>
                </div>

                <div className="d-grid">
                  <button
                    className={`btn ${isPromoted ? "btn-secondary" : "btn-success"} btn-lg fw-bold shadow d-flex align-items-center justify-content-center`}
                    onClick={handleFinalSave}
                    disabled={isProcessing || isSaving || isPromoted}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        SAVING WORKFLOW...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${isPromoted ? "bi-check-circle-fill" : "bi-cloud-check"} me-2`}></i>
                        SAVE & CONCLUDE WORKFLOW
                      </>
                    )}
                  </button>

                  {(isPromoted || saveMessage) && (
                    <div className="text-center animate__animated animate__fadeInUp">
                      {isPromoted && (
                        <p className="text-success small fw-bold mt-2 mb-1">
                          File uploaded successfully{promoBatchId ? ` (Batch #${promoBatchId})` : ""}.
                        </p>
                      )}
                      <p className={`small mb-0 ${isPromoted ? "text-muted" : "text-danger"}`}>
                        {saveMessage}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}